import { createAction, createReducer, Dispatch, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'ducks';
import { injectReducer } from 'ducks/configureStore';
import {
  Feature,
  RenderLocation,
  RendersComponent,
  ShouldReset
} from '../../../../features/Feature';
import { TVSheets } from './sheets/TVSheets';
import { ProductType } from '@inter-ikea-storageone/range';
import TVAction from './TVAction';

export const tvSizes = [32, 42, 48, 55, 65, 75, 85];

export const tvBenchProductTypes = [
  ProductType.BESTA_TV_BENCH_1200_ASM,
  ProductType.BESTA_TV_BENCH_1800_ASM,
  ProductType.EKET_MEDIA_FRAME
];

export const supportsStandingTv = [
  ProductType.BESTA_TV_BENCH_1200_ASM,
  ProductType.BESTA_TV_BENCH_1800_ASM
];

export enum TvPlacement {
  wall = 'wall',
  foot = 'foot'
}

interface TVState {
  addTVSheetVisible: boolean;
  changeTVSheetVisible: boolean;
  tvCollisionSheetVisible: boolean;
  tvSize?: number | undefined;
  tvPlacement?: TvPlacement | undefined;
}

const initialState: TVState = {
  addTVSheetVisible: false,
  changeTVSheetVisible: false,
  tvCollisionSheetVisible: false,
  tvSize: undefined,
  tvPlacement: undefined
};

type ExtendedRootState = RootState & {
  tv?: TVState;
};

export const showAddTVSheet = createAction('tv/showAddTVSheet');
export const hideAddTVSheet = createAction('tv/hideAddTVSheet');
export const showChangeTVSheet = createAction('tv/showChangeTVSheet');
export const hideChangeTVSheet = createAction('tv/hideChangeTVSheet');
export const showCollisionTVSheet = createAction('tv/showCollisionTVSheet');
export const hideCollisionTVSheet = createAction('tv/hideCollisionTVSheet');
export const setTvSize = createAction<number | undefined>('tv/setTvSize');
export const setTvPlacement = createAction<TvPlacement | undefined>('tv/setTvPlacement');
export const resetTvOptions = createAction('tv/resetTvOptions');

export const isAddTVSheetVisible = (state: ExtendedRootState) => state.tv?.addTVSheetVisible;
export const isChangeTVSheetVisible = (state: ExtendedRootState) => state.tv?.changeTVSheetVisible;
export const isTvCollisionSheetVisible = (state: ExtendedRootState) =>
  state.tv?.tvCollisionSheetVisible;
export const getTvSize = (state: ExtendedRootState) => state.tv?.tvSize;
export const getTvPlacement = (state: ExtendedRootState) => state.tv?.tvPlacement;

const tvReducer = createReducer(initialState, builder => {
  builder
    .addCase(showAddTVSheet, state => {
      state.addTVSheetVisible = true;
    })
    .addCase(hideAddTVSheet, state => {
      state.addTVSheetVisible = false;
    })
    .addCase(showChangeTVSheet, state => {
      state.changeTVSheetVisible = true;
    })
    .addCase(hideChangeTVSheet, state => {
      state.changeTVSheetVisible = false;
    })
    .addCase(showCollisionTVSheet, state => {
      state.tvCollisionSheetVisible = true;
    })
    .addCase(hideCollisionTVSheet, state => {
      state.tvCollisionSheetVisible = false;
    })
    .addCase(setTvSize, (state, action: PayloadAction<number | undefined>) => {
      state.tvSize = action.payload;
    })
    .addCase(setTvPlacement, (state, action: PayloadAction<TvPlacement | undefined>) => {
      state.tvPlacement = action.payload;
    })
    .addCase(resetTvOptions, state => {
      state.tvSize = initialState.tvSize;
      state.tvPlacement = initialState.tvPlacement;
    });
});

export class TVFeature extends Feature implements RendersComponent, ShouldReset {
  protected init(): void {
    injectReducer<TVState>('tv', tvReducer);
  }

  render(location: RenderLocation) {
    switch (location) {
      case RenderLocation.Root:
        return TVSheets;
      case RenderLocation.Action:
        return TVAction;
    }
  }

  reset(dispatch: Dispatch): void {
    dispatch(resetTvOptions());
  }
}
